import {
    IAggregatedColumn,
    IAggregatedColumnBinding,
    isUnfilterableBinding,
    parseDisplayNumber,
} from "../../utilities/AggregatedColumnHelper";
import { buildLinearRecursiveCAML, escapeXml } from "../Panels/CMFileFilterPanel/camlUtils";
import type { IAggregatedLibraryDescriptor } from "../Services/AggregatedDocumentsService";
import type { IAggregatedColumnFilter } from "./useAggregatedDocuments";

/**
 * Per-library CAML translation of the aggregate view's column filters. Hoisted out of
 * useAggregatedDocuments into this pure module (type-only imports keep it off the auth
 * chain) so the riskiest translation rules are unit-testable.
 */

// Types whose picked values filter with <Eq> and their own wire type; everything else
// (user/lookup/taxonomy/multi) uses <Contains Type="Text"> — the same probe shape the
// existing filter-value service uses for arbitrary fields. Numeric values are
// normalized via parseDisplayNumber to the invariant stored value or the Eq never
// matches (the section builder harvests numeric values from the invariant dotted raw
// twin, so its values pass through parseDisplayNumber unchanged; display-string
// values from other sources are best-effort disambiguated).
const EQ_FILTER_TYPES = new Set(["Text", "Choice", "Number", "Currency", "Integer", "Counter"]);
const NUMERIC_EQ_TYPES = new Set(["Number", "Currency", "Integer", "Counter"]);

/** A binding whose picked values are numeric on the wire (harvest must use the invariant raw twin). */
export const isNumericEqBinding = (binding: IAggregatedColumnBinding | undefined): boolean =>
    !!binding && NUMERIC_EQ_TYPES.has(binding.type);

/**
 * Builds the <Where> content for ONE library from the active merged-column filters,
 * resolving each filtered column through that library's binding. Returns:
 *  - a CAML string (possibly "") when the library can satisfy every filter,
 *  - null when the library cannot contain matching documents — because a filtered
 *    column has NO binding here, or because none of a filter's values could be
 *    translated for this binding (e.g. non-numeric text picked against a Number
 *    binding of a type-conflicted column). Excluding the library (surfaced via the
 *    FilterExcludesLibraries badge) is the only non-silent outcome; contributing an
 *    empty clause instead would let every document in the library through unfiltered.
 */
export const buildLibraryWhereXml = (
    library: IAggregatedLibraryDescriptor,
    merged: IAggregatedColumn[],
    activeFilters: IAggregatedColumnFilter[]
): string | null => {
    const filterClauses: string[] = [];

    for (const filter of activeFilters ?? []) {
        const column = merged.find(candidate => candidate.key === filter.columnKey);
        const binding = column?.bindings[library.listId];
        if (!binding) {
            return null;
        }

        if (isUnfilterableBinding(binding)) {
            // Unreachable through the filter panel (it hides these columns); if hit
            // programmatically, skipping is safer than emitting CAML SharePoint
            // rejects — that would fail the whole library page.
            continue;
        }

        const conditions: string[] = [];
        for (const value of filter.values ?? []) {
            if (EQ_FILTER_TYPES.has(binding.type)) {
                const wireValue = NUMERIC_EQ_TYPES.has(binding.type) ? parseDisplayNumber(value) : value;
                if (wireValue === null || wireValue === undefined) {
                    continue;
                }
                conditions.push(`<Eq><FieldRef Name="${escapeXml(binding.internalName)}"/><Value Type="${escapeXml(binding.type)}">${escapeXml(String(wireValue))}</Value></Eq>`);
            } else {
                conditions.push(`<Contains><FieldRef Name="${escapeXml(binding.internalName)}"/><Value Type="Text">${escapeXml(value)}</Value></Contains>`);
            }
        }
        if (filter.containsText?.trim()) {
            conditions.push(`<Contains><FieldRef Name="${escapeXml(binding.internalName)}"/><Value Type="Text">${escapeXml(filter.containsText.trim())}</Value></Contains>`);
        }

        const hasCriteria = (filter.values?.length ?? 0) > 0 || !!filter.containsText?.trim();
        if (hasCriteria && conditions.length === 0) {
            // Every picked value failed to translate for this binding: the filter is
            // unsatisfiable here, so the library must be EXCLUDED. (Before this guard,
            // the empty clause was silently dropped and the whole library passed
            // unfiltered — the inverse of what the user asked for.)
            return null;
        }

        const clause = buildLinearRecursiveCAML(conditions, "Or");
        if (clause) {
            filterClauses.push(clause);
        }
    }

    return buildLinearRecursiveCAML(filterClauses, "And");
};
